import { useCallback, useEffect, useRef, useState } from 'react';

import { WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { resolveApiError } from '../api-validation-utils';
import { getHtmlTemplatePreview } from '../html-template.api';
import { PreviewTrigger } from '../types';

import type { ApiErrorDetails, Layout, Orientation, TemplateType, TrackWorkflowFunction } from '../types';

type HtmlTemplatePreviewResult = {
  previewUrl: string;
  isLoading: boolean;
  fetchError: boolean;
  apiErrorDetails: ApiErrorDetails | null;
  refetch: (
    htmlContent: string,
    sourceIds: number[] | null,
    layout: Layout,
    orientation: Orientation,
    trigger?: PreviewTrigger,
  ) => Promise<void>;
};

export const useGetHtmlTemplatePreview = (
  templateType: TemplateType,
  initialPreviewUrl?: string | null,
  hasInitialFetch?: boolean | null,
  tracking?: TrackWorkflowFunction,
): HtmlTemplatePreviewResult => {
  const [previewUrl, setPreviewUrl] = useState(initialPreviewUrl || '');
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(!!hasInitialFetch);
  const [apiErrorDetails, setApiErrorDetails] = useState<ApiErrorDetails | null>(null);
  const previousBlobUrl = useRef('');
  const currentGeneration = useRef(0);

  const refetch = useCallback(
    async (
      htmlContent: string,
      sourceIds: number[] | null,
      layout: Layout,
      orientation: Orientation,
      trigger: PreviewTrigger = PreviewTrigger.Automatic,
    ) => {
      if (!htmlContent || htmlContent.trim() === '') {
        setPreviewUrl('');
        setFetchError(false);
        setApiErrorDetails(null);
        return;
      }
      const generation = ++currentGeneration.current;

      try {
        tracking?.(WorkflowSteps.REFRESH_PREVIEW, { trigger });

        setIsLoading(true);
        setFetchError(false);
        setApiErrorDetails(null);
        const data = await getHtmlTemplatePreview(templateType, htmlContent, sourceIds, layout, orientation);

        if (currentGeneration.current !== generation) return;

        const blob = new Blob([data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        if (previousBlobUrl.current) {
          URL.revokeObjectURL(previousBlobUrl.current);
        }
        previousBlobUrl.current = blobUrl;
        setPreviewUrl(blobUrl);
      } catch (error: unknown) {
        if (currentGeneration.current !== generation) return;

        const errorDetails = resolveApiError(error);
        if (errorDetails) {
          setApiErrorDetails(errorDetails);
        }
        setFetchError(true);
      } finally {
        if (currentGeneration.current === generation) {
          setIsLoading(false);
        }
      }
    },
    [templateType, tracking],
  );

  useEffect(() => {
    return () => {
      if (previousBlobUrl.current) {
        URL.revokeObjectURL(previousBlobUrl.current);
      }
    };
  }, []);

  return { previewUrl, isLoading, fetchError, apiErrorDetails, refetch };
};
